import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import useNotificationPermission from 'hooks/useNotificationPermission';
import { getBrowserIsNotificationCompatible as isNotificationCompatible } from 'modules/app/selectors';
import { openPermissionModal } from 'modules/notifications/actions';
import { getCurrentUserId, hasPushNotificationsEnabled } from 'modules/users/selectors/currentUser';
import { Alert } from 'ui/base';
import { b } from 'utils/i18nUtils';

/** @type {React.FC<{ onClick?: () => void }>} */
const Warning = ({ onClick }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { hasUserDenied } = useNotificationPermission();

  const userId = useSelector(getCurrentUserId);
  const hasNotificationsEnabled = useSelector(hasPushNotificationsEnabled);
  const isBrowserCompatible = useSelector(isNotificationCompatible);
  const showActivate = hasUserDenied && isBrowserCompatible && Notification?.permission === 'default';

  const handleOnClick = event => {
    event.preventDefault();
    dispatch(openPermissionModal(userId));
    if (typeof onClick === 'function') {
      onClick();
    }
  };

  let message = null;
  let description = null;

  if (!isBrowserCompatible) {
    message = 'notifications.warning.incompatibleBrowserTitle';
    description = {
      id: 'notifications.warning.incompatibleBrowser',
      defaultMessage:
        'To receive real-time notifications, please use another browser such as <b>Chrome</b> or <b>Firefox</b>.',
      values: {
        b,
      },
    };
  } else if (showActivate) {
    message = 'notifications.warning.disabledNotificationsTitle';
    description = {
      id: 'notifications.warning.deniedNotifications',
      defaultMessage: '<a>Click here</a> to activate notifications in your browser.',
      values: {
        // eslint-disable-next-line react/no-unstable-nested-components
        a: chunks => <a onClick={handleOnClick}>{chunks}</a>,
      },
    };
  } else if (!hasNotificationsEnabled) {
    message = 'notifications.warning.disabledNotificationsTitle';
    description = {
      id: 'notifications.warning.disabledNotifications',
      defaultMessage: "If you don't know how, you can find information <a>here</a>.",
      values: {
        // eslint-disable-next-line react/no-unstable-nested-components
        a: chunks => (
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={intl.formatMessage({
              id: 'notifications.helpLink',
              defaultMessage: 'https://campus.fleetback.com/faq/',
            })}
          >
            {chunks}
          </a>
        ),
      },
    };
  }

  return (
    description &&
    <Alert
      type="warning"
      className="mx-4 mb-4"
      title={<FormattedMessage id={message} />}
      description={<FormattedMessage {...description} />}
    />
  );
};

export default Warning;
